'use client'

import * as React from 'react'
import { Search, Download, Filter } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../../firebase'

const actionColors = {
  CREATE: 'bg-success/15 text-success',
  UPDATE: 'bg-primary/15 text-primary',
  DELETE: 'bg-destructive/15 text-destructive',
  SUBMIT: 'bg-warning/15 text-warning',
  APPROVE: 'bg-success/15 text-success',
  REJECT: 'bg-destructive/15 text-destructive',
  SYNC: 'bg-muted text-muted-foreground',
}

const normalizeAction = (value) => {
  const raw = String(value || '').trim()
  return raw || 'Update'
}

const getActionKey = (action) => {
  const upper = String(action || '').toUpperCase()
  if (upper.includes('CREATE') || upper.includes('ADD')) return 'CREATE'
  if (upper.includes('UPDATE') || upper.includes('EDIT')) return 'UPDATE'
  if (upper.includes('DELETE') || upper.includes('REMOVE')) return 'DELETE'
  if (upper.includes('SUBMIT')) return 'SUBMIT'
  if (upper.includes('APPROVE')) return 'APPROVE'
  if (upper.includes('REJECT')) return 'REJECT'
  if (upper.includes('SYNC')) return 'SYNC'
  return upper.split(' ')[0] || 'UPDATE'
}

const getActionTone = (actionKey) => actionColors[actionKey] || 'bg-muted text-muted-foreground'

const parseDate = (value) => {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatDateTime = (value) => {
  const date = parseDate(value)
  if (!date) return '—'
  return date.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDayLabel = (date) => date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric' })

export default function AuditPage() {
  const [logs, setLogs] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [search, setSearch] = React.useState('')
  const [actionFilter, setActionFilter] = React.useState('all')
  const [entityFilter, setEntityFilter] = React.useState('all')

  React.useEffect(() => {
    let isMounted = true

    async function loadLogs() {
      try {
        const snapshot = await getDocs(collection(db, 'auditLogs'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        setLogs(records)
      } catch {
        if (!isMounted) return
        setLogs([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    loadLogs()

    return () => {
      isMounted = false
    }
  }, [])

  const normalizedLogs = logs
    .map((log) => {
      const action = normalizeAction(log.action)
      const actionKey = getActionKey(action)
      const entity = String(log.entity || log.resourceType || 'System')
      const user = String(log.userDisplayName || log.user || log.userName || log.userEmail || 'System')
      const details =
        log.details ||
        log.description ||
        log.message ||
        log.changes?.after ||
        log.changes?.before ||
        ''
      return {
        ...log,
        action,
        actionKey,
        entity,
        user,
        details: String(details || ''),
        timestamp: log.timestamp || log.createdAt,
      }
    })
    .sort((a, b) => {
      const dateA = parseDate(a.timestamp)?.getTime() || 0
      const dateB = parseDate(b.timestamp)?.getTime() || 0
      return dateB - dateA
    })

  const filteredLogs = normalizedLogs.filter((entry) => {
    const searchValue = search.toLowerCase()
    const matchesSearch =
      entry.user.toLowerCase().includes(searchValue) ||
      entry.action.toLowerCase().includes(searchValue) ||
      entry.entity.toLowerCase().includes(searchValue) ||
      String(entry.entityId || '').toLowerCase().includes(searchValue) ||
      entry.details.toLowerCase().includes(searchValue)
    const matchesAction = actionFilter === 'all' || entry.actionKey === actionFilter
    const matchesEntity = entityFilter === 'all' || entry.entity === entityFilter
    return matchesSearch && matchesAction && matchesEntity
  })

  const uniqueUsers = new Set(normalizedLogs.map((log) => log.user)).size
  const uniqueEntities = new Set(normalizedLogs.map((log) => log.entity)).size

  const now = new Date()
  const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)
  const last7Days = normalizedLogs.filter((log) => {
    const date = parseDate(log.timestamp)
    return date && date >= sevenDaysAgo
  })

  const actionCounts = normalizedLogs.reduce((acc, log) => {
    acc[log.actionKey] = (acc[log.actionKey] || 0) + 1
    return acc
  }, {})

  const actionChartData = Object.entries(actionCounts).map(([name, value], index) => ({
    name,
    value,
    color: ['oklch(0.35 0.12 145)', 'oklch(0.55 0.16 150)', 'oklch(0.87 0.15 85)', 'oklch(0.7 0.17 65)', 'oklch(0.577 0.245 27.325)'][index % 5],
  }))

  const entityCounts = normalizedLogs.reduce((acc, log) => {
    acc[log.entity] = (acc[log.entity] || 0) + 1
    return acc
  }, {})

  const entityChartData = Object.entries(entityCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8)

  const activityData = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (6 - index))
    const label = formatDayLabel(date)
    const count = normalizedLogs.filter((log) => {
      const logDate = parseDate(log.timestamp)
      return logDate && logDate.toDateString() === date.toDateString()
    }).length
    return { day: label, count }
  })

  const actionOptions = Array.from(new Set(normalizedLogs.map((log) => log.actionKey))).sort()
  const entityOptions = Array.from(new Set(normalizedLogs.map((log) => log.entity))).sort()

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">Audit Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">System-wide audit trail and change history</p>
        </div>
        <Button variant="outline" className="gap-2 text-foreground">
          <Download className="size-4" />
          Export CSV
        </Button>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="bg-muted">
          <TabsTrigger value="dashboard" className="text-xs">Audit Overview</TabsTrigger>
          <TabsTrigger value="trail" className="text-xs">Audit Trail</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4 flex flex-col gap-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{loading ? '—' : normalizedLogs.length}</p>
                <p className="text-xs text-muted-foreground font-medium mt-1">Total Events</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
              </CardContent>
            </Card>
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{loading ? '—' : uniqueUsers}</p>
                <p className="text-xs text-muted-foreground font-medium mt-1">Unique Users</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted-foreground/30" />
              </CardContent>
            </Card>
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{loading ? '—' : uniqueEntities}</p>
                <p className="text-xs text-muted-foreground font-medium mt-1">Entities Touched</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-muted-foreground/30" />
              </CardContent>
            </Card>
            <Card className="border border-border relative overflow-hidden">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-foreground">{loading ? '—' : last7Days.length}</p>
                <p className="text-xs text-muted-foreground font-medium mt-1">Last 7 Days</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary" />
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Actions Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={actionChartData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3} strokeWidth={0}>
                      {actionChartData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
                  {actionChartData.map((entry) => (
                    <div key={entry.name} className="flex items-center gap-1.5">
                      <span className="size-2 rounded-full" style={{ backgroundColor: entry.color }} />
                      <span className="text-[10px] text-muted-foreground">{entry.name} ({entry.value})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border border-border lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-foreground">Entities with Most Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={entityChartData} barSize={18}>
                    <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                    <Bar dataKey="value" fill="oklch(0.35 0.12 145)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-foreground">Activity (Last 7 Days)</CardTitle>
              <CardDescription className="text-xs">Number of audit events per day</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={activityData} barSize={18}>
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                  <YAxis tick={{ fontSize: 10 }} stroke="oklch(0.48 0.03 145)" />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                  <Bar dataKey="count" fill="oklch(0.55 0.16 150)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trail" className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search audit logs..."
                className="pl-10 h-9"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger className="w-[150px] h-9">
                <Filter className="mr-2 size-3.5" />
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionOptions.map((action) => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[150px] h-9">
                <SelectValue placeholder="Entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityOptions.map((entity) => (
                  <SelectItem key={entity} value={entity}>{entity}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="border border-border">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="border-b border-border">
                    <tr className="text-xs text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Timestamp</th>
                      <th className="px-4 py-3 font-medium">User</th>
                      <th className="px-4 py-3 font-medium">Action</th>
                      <th className="px-4 py-3 font-medium">Entity</th>
                      <th className="px-4 py-3 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">
                          Loading audit logs...
                        </td>
                      </tr>
                    ) : filteredLogs.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">
                          No audit logs found.
                        </td>
                      </tr>
                    ) : (
                      filteredLogs.map((entry) => (
                        <tr key={entry.id} className="border-b border-border last:border-0">
                          <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                            {formatDateTime(entry.timestamp)}
                          </td>
                          <td className="px-4 py-3 text-xs text-foreground">{entry.user}</td>
                          <td className="px-4 py-3">
                            <Badge className={`text-[10px] ${getActionTone(entry.actionKey)}`}>
                              {entry.action}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {entry.entity}
                            {entry.entityId ? <span className="text-[10px] text-muted-foreground"> ({entry.entityId})</span> : null}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground max-w-[320px]">
                            {entry.changes?.before || entry.changes?.after ? (
                              <div className="space-y-0.5">
                                {entry.changes?.before && (
                                  <div className="flex gap-1">
                                    <span className="text-red-600 line-through">{entry.changes.before}</span>
                                  </div>
                                )}
                                {entry.changes?.after && (
                                  <div className="flex gap-1">
                                    <span className="text-emerald-600">{entry.changes.after}</span>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="truncate block">{entry.details || '—'}</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
